// <---- Core Imports ---->
import { useEffect, useState, lazy, Suspense } from "react";
import { Navigate, Route, Routes, useParams } from "react-router-dom";

// <---- Context and Provider Imports ---->
import { PurchasesProvider } from "@/context/purchases";
import { SocketProvider } from "@/providers/socket.provider";

// <---- Redux Store Imports ---->
import { useAppSelector } from "@/hooks/redux";
import { EmployerPermissions } from "@/types/user";
import { Loading } from "./components/ui/loading";
import { PERMISSIONS } from "./constants";
import SendRequestWithoutJobPostPage from "./pages/dashboard/employer/VerificationChecks/SendRequestWithoutJobPost";
import { Community } from "./pages/common/Community";
import { PostDetails } from "./pages/common/Community/PostDetails";
import { EventDetails } from "./pages/common/Community/EventDetails";
import RequireEmployerPermission from "./routes/RequireEmployerPermission";

// <---- Lazy Imports ---->
const Subscription = lazy(() => import("@/pages/subscription"));
const ProtectedRoutes = lazy(() => import("@/routes/ProtectedRoutes"));
const RedirectRoutes = lazy(() => import("@/routes/RedirectRoutes"));
const NoInternet = lazy(() => import("@/pages/error/NoInternet"));
const NotFound = lazy(() => import("@/pages/error/NotFound"));
const DynamicLinkHandler = lazy(() => import("@/pages/common/DynamicLink"));
const TradesPassportPublic = lazy(() => import("@/pages/passport"));

const ForgotPassword = lazy(() => import("@/pages/auth/ForgotPassword"));
const Login = lazy(() => import("@/pages/auth/Login"));
const ResetPassword = lazy(() => import("@/pages/auth/ResetPassword"));
const SignUp = lazy(() => import("@/pages/auth/Signup"));
const JobSeekerAddress = lazy(() => import("@/pages/onboarding/Address"));
const VerifyEmail = lazy(() => import("@/pages/onboarding/VerifyEmail"));
const VerifyTaxID = lazy(() => import("@/pages/onboarding/VerifyTaxID"));
const JobSeekerReferrals = lazy(() => import("@/pages/onboarding/Referrals/JobSeekerReferrals"));
const JobSeekerQuickstart = lazy(() => import("@/pages/onboarding/JobSeekerQuickstart"));
const EmployerReferrals = lazy(() => import("@/pages/onboarding/Referrals/EmployerReferrals"));

const Chats = lazy(() => import("@/pages/common/Chats"));
const ChangeEmail = lazy(() => import("@/pages/common/Account/Email"));
const ChangePassword = lazy(() => import("@/pages/common/Account/Password"));
const EditCardInformation = lazy(() => import("@/pages/common/Account/CardInformation"));
const PersonalInfo = lazy(() => import("@/pages/common/Account/PersonalInfo"));
const ContactUs = lazy(() => import("@/pages/common/ContactUs"));
const FAQs = lazy(() => import("@/pages/common/FAQs"));
const HelpCenter = lazy(() => import("@/pages/common/HelpCenter"));
const PrivacyPolicy = lazy(() => import("@/pages/common/PrivacyPolicy"));
const Settings = lazy(() => import("@/pages/common/Settings"));
const TermsAndConditions = lazy(() => import("@/pages/common/TermsAndConditions"));

const EmployerRootLayout = lazy(() => import("@/layouts/employer"));
const EmployerAccountLayout = lazy(() => import("@/layouts/employer/Account"));
const JobseekerRootLayout = lazy(() => import("@/layouts/jobseeker"));
const JobseekerAccountLayout = lazy(() => import("@/layouts/jobseeker/Account"));
const JobseekerProfileLayout = lazy(() => import("@/layouts/jobseeker/Profile"));

const EmployerHome = lazy(() => import("@/pages/dashboard/employer/Home"));
const CandidateDetails = lazy(() => import("@/pages/dashboard/employer/Home/CandidateDetails"));
const CandidateSearch = lazy(() => import("@/pages/dashboard/employer/Home/CandidateSearch"));
const ApplicationDetail = lazy(
  () => import("@/pages/dashboard/employer/Jobs/Applications/ApplicationDetail"),
);
const PostJob = lazy(() => import("@/pages/dashboard/employer/Jobs/PostJob"));
const EmployerProfile = lazy(() => import("@/pages/dashboard/employer/Profile"));
const CreateInterview = lazy(
  () => import("@/pages/dashboard/employer/Jobs/ScheduleInterviews/Create"),
);
const EditInterview = lazy(() => import("@/pages/dashboard/employer/Jobs/ScheduleInterviews/Edit"));
const Jobs = lazy(() => import("@/pages/dashboard/employer/Jobs"));
const Applications = lazy(() => import("@/pages/dashboard/employer/Jobs/Applications"));
const ArchivedApplications = lazy(
  () => import("@/pages/dashboard/employer/Jobs/Applications/Archived"),
);
const ShortlistedApplications = lazy(
  () => import("@/pages/dashboard/employer/Jobs/Applications/Shortlisted"),
);
const EditJob = lazy(() => import("@/pages/dashboard/employer/Jobs/EditJob"));
const JobDetail = lazy(() => import("@/pages/dashboard/employer/Jobs/JobDetail"));
const StarredCandidates = lazy(() => import("@/pages/dashboard/employer/StarredCandidates"));
const RequestedCandidate = lazy(() => import("@/pages/dashboard/employer/Jobs/RequestedCandidate"));
const EmployerScheduledInterviews = lazy(
  () => import("@/pages/dashboard/employer/Jobs/ScheduleInterviews"),
);

const ZoomMeet = lazy(() => import("@/pages/dashboard/jobseeker/Interviews/Zoom"));
const JobseekerHome = lazy(() => import("@/pages/dashboard/jobseeker/Jobs"));
// Unified Applied / Saved / Interviews hub — all three routes render it and it
// picks the active tab from the URL (keeps deep links + sidebar active state).
const MyJobs = lazy(() => import("@/pages/dashboard/jobseeker/MyJobs"));
const ApplyJob = lazy(() => import("@/pages/dashboard/jobseeker/Jobs/Apply"));
const JobsDetails = lazy(() => import("@/pages/dashboard/jobseeker/Jobs/Details"));
const ReviewApplication = lazy(() => import("@/pages/dashboard/jobseeker/Jobs/Review"));
const JobseekerJobDetails = lazy(() => import("@/pages/dashboard/jobseeker/Profile/JobDetails"));
const JobseekerPersonalInformation = lazy(
  () => import("@/pages/dashboard/jobseeker/Profile/PersonalInformation"),
);
const JobseekerPortfolio = lazy(() => import("@/pages/dashboard/jobseeker/Profile/Portfolio"));
const JobseekerProfessionalDetails = lazy(
  () => import("@/pages/dashboard/jobseeker/Profile/ProfessionalDetails"),
);
const JobseekerEmployerProfile = lazy(() => import("@/pages/dashboard/jobseeker/Profile/Employer"));
const JobseekerUserProfile = lazy(() => import("@/pages/dashboard/jobseeker/Profile/User"));
const EmployerOrganizationProfile = lazy(() => import("@/pages/dashboard/employer/Organization"));

const ZoomMeeting = lazy(() => import("@/pages/dashboard/ZoomMeeting/ZoomMeeting"));
const VerficationChecksPricing = lazy(
  () => import("./pages/dashboard/jobseeker/VerificationChecks/Pricing"),
);
const VerificationApplicationForm = lazy(
  () => import("./pages/dashboard/jobseeker/VerificationChecks/Application"),
);
const BackgroundChecksDashboard = lazy(
  () => import("./pages/dashboard/jobseeker/VerificationChecks/Dashboard"),
);
const BackgroundApplicationDetail = lazy(
  () => import("./pages/dashboard/jobseeker/VerificationChecks/ReportDetail"),
);
const JobseekerApplicationPreview = lazy(
  () => import("./pages/dashboard/jobseeker/VerificationChecks/ApplicationPreview"),
);
const EmployerVerficationChecksPricing = lazy(
  () => import("./pages/dashboard/employer/VerificationChecks/Pricing"),
);
const VerificationChecksLayout = lazy(() => import("./layouts/jobseeker/VerificationChecks"));
const SendRequestPage = lazy(
  () => import("./pages/dashboard/employer/VerificationChecks/SendRequests"),
);
const BackgroundChecksOrganizationDashboard = lazy(
  () => import("./pages/dashboard/employer/VerificationChecks/Dashboard"),
);
const BackgroundReportDetail = lazy(
  () => import("./pages/dashboard/employer/VerificationChecks/ReportDetail"),
);
const VerificationApplicationDetail = lazy(
  () => import("./pages/dashboard/employer/VerificationChecks/ApplicationDetail"),
);

function CommunityPostRedirect() {
  const { postId } = useParams();
  return <Navigate to={`../community/post/${postId}`} replace />;
}

function CommunityEventRedirect() {
  const { eventId } = useParams();
  return <Navigate to={`../community/event/${eventId}`} replace />;
}

function App() {
  const user = useAppSelector((state) => state?.auth?.userData);
  const [isOnline, setIsOnline] = useState(window.navigator.onLine);

  useEffect(() => {
    const handleNetworkChange = () => {
      setIsOnline(window.navigator.onLine);
    };

    window.addEventListener("online", handleNetworkChange);
    window.addEventListener("offline", handleNetworkChange);

    return () => {
      window.removeEventListener("online", handleNetworkChange);
      window.removeEventListener("offline", handleNetworkChange);
    };
  }, []);

  if (!isOnline) {
    return <NoInternet />;
  }

  return (
    <SocketProvider
      existingRefreshToken={user && user.refresh_token ? user.refresh_token : ""}
      initialAccessToken={user && user.access_token ? user.access_token : ""}
    >
      <Suspense fallback={<Loading />}>
        <Routes>
          {/* <-------- DYNAMIC LINK HANDLER --------> */}
          <Route path="/d/:code" element={<DynamicLinkHandler />} />

          {/* <-------- PUBLIC TRADES PASSPORT --------> */}
          <Route path="/passport/:token" element={<TradesPassportPublic />} />

          {/* <-------- PASSWORD RECOVERY AND VERIFICATION AND ONBOARDING PAGES --------> */}
          <Route path="/" element={<Navigate to="/login" />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/verify-tax-id" element={<VerifyTaxID />} />
          <Route path="/signup/address" element={<JobSeekerAddress />} />
          <Route path="/signup/user-referrals" element={<JobSeekerReferrals />} />
          <Route path="/signup/jobseeker/quickstart" element={<JobSeekerQuickstart />} />
          <Route path="/signup/referrals" element={<EmployerReferrals />} />

          {/* <-------- AUTH PAGES --------> */}
          <Route element={<RedirectRoutes />}>
            <Route index path="/login" element={<Login />} />
            <Route path="/signup" element={<SignUp />} />
          </Route>

          {/* <-------- EMPLOYER DASHBOARD --------> */}
          <Route element={<ProtectedRoutes roles={["employer"]} />}>
            <Route
              path="/employer"
              element={
                <PurchasesProvider>
                  <EmployerRootLayout />
                </PurchasesProvider>
              }
            >
              <Route index element={<EmployerHome />} />
              <Route path="jobs" element={<Jobs />} />
              <Route path="jobs/:id" element={<JobDetail />} />
              <Route path="jobs/:id/edit" element={<EditJob />} />
              <Route path="jobs/:id/applications" element={<Applications />} />
              <Route
                path="jobs/:id/applications/shortlisted"
                element={<ShortlistedApplications />}
              />
              <Route path="jobs/:id/applications/archived" element={<ArchivedApplications />} />
              <Route path="jobs/:id/applications/:applicationId" element={<ApplicationDetail />} />
              <Route path="starred-candidates" element={<StarredCandidates />} />
              <Route path="requested-candidates" element={<RequestedCandidate />} />
              <Route path="scheduled-interviews" element={<EmployerScheduledInterviews />} />
              {/* TODO: Check below route */}
              <Route path="scheduled-interviews/zoom" element={<ZoomMeeting />} />
              <Route path="interviews/:id/edit" element={<EditInterview />} />
              <Route path="applications/:id/create-interview" element={<CreateInterview />} />

              <Route path="jobseekers" element={<CandidateSearch />} />
              <Route path="jobseekers/:id" element={<CandidateDetails />} />

              <Route path="post-job" element={<PostJob />} />

              {/* <--- Chat Pages ---> */}
              <Route
                element={
                  <RequireEmployerPermission
                    permission={EmployerPermissions.jobs}
                    permissionName={PERMISSIONS.chats}
                  />
                }
              >
                <Route path="chats" element={<Chats />} />
                <Route path="chats/:id" element={<Chats />} />
              </Route>

              {/* <--- Profile Pages ---> */}
              <Route path="profile" element={<EmployerProfile />} />

              {/* <--- View Other Profiles ---> */}
              <Route path="organizations/:id" element={<EmployerOrganizationProfile />} />

              {/* <-------- COMMON PAGES --------> */}
              <Route path="account" element={<EmployerAccountLayout />}>
                <Route path="personal-info" element={<PersonalInfo />} />
                <Route path="change-email" element={<ChangeEmail />} />
                <Route path="change-password" element={<ChangePassword />} />
                <Route path="edit-card-information" element={<EditCardInformation />} />
              </Route>
              <Route path="help-center" element={<HelpCenter />} />
              <Route path="faqs" element={<FAQs />} />
              <Route path="contact-us" element={<ContactUs />} />
              <Route path="privacy-policy" element={<PrivacyPolicy />} />
              <Route path="terms-and-conditions" element={<TermsAndConditions />} />
              <Route path="settings" element={<Settings />} />
              <Route path="verification-checks" element={<VerificationChecksLayout />}>
                <Route index element={<Navigate to="pricing" replace />} />
                <Route path="pricing" element={<EmployerVerficationChecksPricing />} />
                <Route path="pricing/:id" element={<SendRequestPage />} />
                <Route
                  path="pricing/without-job-post/:id"
                  element={<SendRequestWithoutJobPostPage />}
                />
                <Route path="dashboard" element={<BackgroundChecksOrganizationDashboard />} />
                <Route path="dashboard/:id" element={<BackgroundReportDetail />} />
                <Route path="applications/:id" element={<VerificationApplicationDetail />} />
              </Route>
              {/* <-------- SUBSCRIPTION PAGES --------> */}
              <Route path="subscription" element={<Subscription />} />

              {/* <-------- COMMUNITY PAGES (inside employer layout) --------> */}
              <Route path="community">
                <Route index element={<Community />} />
                <Route path="post/:postId" element={<PostDetails />} />
                <Route path="event/:eventId" element={<EventDetails />} />
              </Route>
              {/* Redirect old newsfeed URLs */}
              <Route path="newsfeed" element={<Navigate to="../community" replace />} />
              <Route path="newsfeed/post/:postId" element={<CommunityPostRedirect />} />
              <Route path="newsfeed/event/:eventId" element={<CommunityEventRedirect />} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Route>

          {/* <-------- JOBSEEKER DASHBOARD --------> */}
          <Route element={<ProtectedRoutes roles={["job_seeker"]} />}>
            <Route path="/jobseeker" element={<JobseekerRootLayout />}>
              {/* <--- Jobs Pages ---> */}
              <Route index element={<JobseekerHome />} />
              {/* Applied / Saved / Interviews share the unified MyJobs hub */}
              <Route path="jobs/applied" element={<MyJobs />} />
              <Route path="jobs/saved" element={<MyJobs />} />
              <Route path="jobs/detail" element={<JobsDetails />} />
              <Route path="jobs/apply" element={<ApplyJob />} />
              <Route path="jobs/review" element={<ReviewApplication />} />
              {/* <--- Interview Pages ---> */}
              <Route path="interviews" element={<MyJobs />} />
              <Route path="interviews/zoom" element={<ZoomMeet />} />
              {/* <--- Chat Pages ---> */}
              <Route path="chats" element={<Chats />} />
              <Route path="chats/:id" element={<Chats />} />
              {/* <--- Profile Pages ---> */}
              <Route path="profile" element={<JobseekerProfileLayout />}>
                <Route path="personal-information" element={<JobseekerPersonalInformation />} />
                <Route path="portfolio" element={<JobseekerPortfolio />} />
                <Route path="job-details" element={<JobseekerJobDetails />} />
                <Route path="professional-details" element={<JobseekerProfessionalDetails />} />
              </Route>

              {/* <--- View Other Profiles ---> */}
              <Route path="users/:id" element={<JobseekerUserProfile />} />
              <Route path="employers/:id" element={<JobseekerEmployerProfile />} />

              {/* <-------- COMMON PAGES --------> */}
              <Route path="account" element={<JobseekerAccountLayout />}>
                <Route path="personal-info" element={<PersonalInfo />} />
                <Route path="change-email" element={<ChangeEmail />} />
                <Route path="change-password" element={<ChangePassword />} />
              </Route>
              {/* <-------- VERIFICATION CHECKS PAGES --------> */}
              <Route path="verification-checks" element={<VerificationChecksLayout />}>
                <Route index element={<Navigate to="pricing" replace />} />
                <Route path="pricing" element={<VerficationChecksPricing />} />
                <Route path="pricing/:id" element={<VerificationApplicationForm />} />
                <Route path="applications/:id" element={<JobseekerApplicationPreview />} />
                <Route path="dashboard" element={<BackgroundChecksDashboard />} />
                <Route path="dashboard/:id" element={<BackgroundApplicationDetail />} />
              </Route>
              <Route path="help-center" element={<HelpCenter />} />
              <Route path="faqs" element={<FAQs />} />
              <Route path="contact-us" element={<ContactUs />} />
              <Route path="privacy-policy" element={<PrivacyPolicy />} />
              <Route path="terms-and-conditions" element={<TermsAndConditions />} />
              <Route path="settings" element={<Settings />} />

              {/* <-------- COMMUNITY PAGES (inside jobseeker layout) --------> */}
              <Route path="community">
                <Route index element={<Community />} />
                <Route path="post/:postId" element={<PostDetails />} />
                <Route path="event/:eventId" element={<EventDetails />} />
              </Route>
              {/* Redirect old newsfeed URLs */}
              <Route path="newsfeed" element={<Navigate to="../community" replace />} />
              <Route path="newsfeed/post/:postId" element={<CommunityPostRedirect />} />
              <Route path="newsfeed/event/:eventId" element={<CommunityEventRedirect />} />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </Suspense>
    </SocketProvider>
  );
}

export default App;
